import { GridWithFilters } from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
    referralGridColumns,
    referralGridOnRowClick,
    referralGridRowCssClass,
} from 'components/shared/ReferralGridLayout';
import { ViewsControls } from 'components/shared/ViewsControls';
import { type BackToDestination } from 'services/navigationHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import { Grids, resetGrid } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';
import { resetReferralData } from 'store/referrals/referralListSlice';
import { fetchReferrals, updatePage } from 'store/referrals/referralListThunks';

interface ReferralGridProps {
    supplierNpi?: string;
    prescriberNpi?: string;
    title?: string;
    parentLocationName: string;
}

/** Note: only one of supplierNpi or prescriberNpi should be supplied */
export const ReferralGrid = ({
    supplierNpi = undefined,
    prescriberNpi = undefined,
    title = undefined,
    parentLocationName,
}: ReferralGridProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { permissions } = useAppSelector((state) => state.auth);

    const { segmentId } = useAppSelector((state) => state.views);

    const { pages, currentPage, referralCount, npi, filteredCount, filterCategories } =
        useAppSelector((state) => state.referralList);
    const currentPageData = pages[currentPage];

    useEffect(() => {
        if (npi !== (supplierNpi ?? prescriberNpi) && currentPageData) {
            dispatch(resetGrid(Grids.REFERRALS));
            dispatch(resetReferralData());
        }
    }, [dispatch, npi, supplierNpi, prescriberNpi, currentPageData]);

    const {
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.REFERRALS);

    const [searching, setSearching] = useState(false);
    useEffect(() => {
        const handler = setTimeout(() => {
            if (searching) {
                setSearching(false);
                dispatch(resetReferralData());
            }
        }, 1000);
        return () => clearTimeout(handler);
    }, [search, searching, dispatch]);

    useEffect(() => {
        if (!currentPageData) {
            dispatch(
                fetchReferrals({
                    segmentId,
                    searchText: search,
                    supplierNpi,
                    prescriberNpi,
                    activeFilters,
                }),
            );
        }
    }, [dispatch, currentPageData, search, supplierNpi, prescriberNpi, segmentId, activeFilters]);

    const backToDestination: BackToDestination = {
        text: parentLocationName,
        route: location.pathname,
    };

    return (
        <GridWithFilters
            title={title}
            headerContent={
                (filteredCount ?? 0) > 0 ? (
                    <div className="margin__bottom--medium">
                        <InlineMessage
                            intent={InlineMessage.Intent.INFO}
                            label={`The ${referralCount} referrals viewable below do not include ${filteredCount} restricted referrals hidden from view.`}
                        />
                    </div>
                ) : undefined
            }
            viewsPaneContent={<ViewsControls />}
            data={currentPageData}
            isLoading={!currentPageData || searching}
            enableSorting={false}
            enableSearch
            searchText={search}
            onSearch={(searchText) => {
                setSearching(true);
                updateGridSearch?.(searchText);
            }}
            filterCategories={filterCategories}
            activeFilters={activeFilters}
            onFilter={(filters) => {
                // The filter pane reports its empty defaults on mount. That is not a user filter
                // change, and flagging it as one clears the page cache a second later and refetches.
                const isMountDefaults =
                    !activeFilters && !Object.values(filters).some((values) => values?.length);
                if (!isMountDefaults) {
                    setSearching(true);
                }
                updateGridActiveFilters?.(filters);
            }}
            filterPaneRenderWidth={filterPaneWidth}
            onFilterPaneResize={updateFilterPaneWidth}
            currentPage={currentPage}
            totalCount={referralCount}
            onPageChange={(page) => dispatch(updatePage(page))}
            onRowClick={referralGridOnRowClick(navigate, backToDestination)}
            rowCssClass={referralGridRowCssClass}
            columns={referralGridColumns(permissions, backToDestination)}
        />
    );
};
